// src/cockpit4/components/AIMoneySOP.tsx
// ============================================
// AI 自主赚钱 SOP（设置中心 · 操作说明）
//
// 说明：XEAOS 如何从 5 个维度实施与运行"系统自己赚钱、人工协助"
// 每个维度：目标 → 操作步骤（对应系统位置） → 自动化程度 → 人工介入点
// ============================================

import { useState } from "react";
import { ChevronDown, Sparkles, Share2, Radar, Handshake, DollarSign } from "lucide-react";

interface Dimension {
  id: string;
  title: string;
  subtitle: string;
  icon: typeof Sparkles;
  color: string;
  steps: string[];
  autoLevel: string;
  human: string;
}

const DIMENSIONS: Dimension[] = [
  {
    id: "content",
    title: "维度 ① 内容流量变现（公众号全自动）",
    subtitle: "AI 写稿 → 自动发布 → 流量主/广告收益",
    icon: Share2,
    color: "#07C160",
    steps: [
      "内容工厂 → 刷新今日计划：系统基于新闻热点/知识库自动选题（每天 3 篇/平台）",
      "浏览各平台条目，满意的点「生成」，不满意的点「换标题」或「换内容」",
      "生成完成 → 点「同步到公众号」：自动生成标题+封面 → 全自动发布（草稿→发布）",
      "收益到账后：财务中心 → 自动收入 → 入账（公众号流量主/广告分成）",
    ],
    autoLevel: "90% 自动（选题/写稿/排版/发布全自动）",
    human: "每天 5 分钟：审核内容 + 点生成/换一篇",
  },
  {
    id: "shortvideo",
    title: "维度 ② 短视频平台（抖音/小红书半自动）",
    subtitle: "AI 生成脚本/图文 → 一键确认发布",
    icon: Sparkles,
    color: "#FF2442",
    steps: [
      "内容工厂 → 今日计划 → 抖音/小红书条目 → 点「生成」（脚本 + 封面）",
      "预览脚本 → 不满意点「换内容」重新生成，满意后点「查看完整脚本」复制",
      "人工到平台 App 粘贴发布（平台 API 限制 AI 直发，这是规则约束）",
      "收益到账（中视频计划/商单）→ 财务中心 → 自动收入 → 入账",
    ],
    autoLevel: "70% 自动（生成脚本/封面全自动，发布需人工确认）",
    human: "每天 10 分钟：确认脚本 + 平台粘贴发布",
  },
  {
    id: "geo",
    title: "维度 ③ AI 可见性获客（GEO 引擎）",
    subtitle: "让大模型主动推荐你的内容/产品",
    icon: Radar,
    color: "#8B5CF6",
    steps: [
      "经营中心 → GEO+DSO → GEO 输入层：设置品牌关键词与知识块",
      "GEO 输出层 → 一键启动 GrowthFactory 编排：自动生成各平台内容",
      "AI 超市 → GEO+DSO 增长工具 → 运行「AI 可见性监测」：向大模型提问并粘贴回答",
      "看板自动解析：提及率/首条占位/前 3 推荐/竞品对比 → 判断心智占有率",
      "按占有率调 SEM/GEO 预算配比（经营中心 → SEM/GEO 预算）",
    ],
    autoLevel: "80% 自动（策略/生成/解析自动，提问回答需人工粘贴）",
    human: "每周 2-3 次：向大模型提问并粘贴回答，看板自动分析",
  },
  {
    id: "leads",
    title: "维度 ④ 线索成交（Hermes 询盘 → 人工成交）",
    subtitle: "系统筛线索、自动应答，你只负责谈单",
    icon: Handshake,
    color: "#3B82F6",
    steps: [
      "Hermes → 对话：告诉它「关注公众号后台/XEAOS 官网的询盘」",
      "Hermes 自动识别高意向线索（企业服务/合作咨询），汇总成线索清单",
      "人工跟进：对高意向线索报价、约谈、成交（成交金额记入财务中心 → 自动收入 → 企业服务）",
    ],
    autoLevel: "50% 自动（识别/汇总自动，成交需人工）",
    human: "每周 20 分钟：处理高价值线索、谈单成交",
  },
  {
    id: "income",
    title: "维度 ⑤ 自动收入记账（变现闭环）",
    subtitle: "所有收益入账 → 看板看见系统赚了多少",
    icon: DollarSign,
    color: "#34D399",
    steps: [
      "财务中心 → 自动收入：选择平台（公众号/抖音/小红书/知乎/带货/企业服务）→ 填金额 → 入账",
      "看板自动统计：本月收入/今日/近 7 天/平台分布/30 天趋势",
      "每周复盘：哪类内容/平台最赚钱 → 经营中心 → 刷新今日计划时加大该方向",
    ],
    autoLevel: "入账后 100% 自动统计（录入需人工，可对接各平台结算导出）",
    human: "每月 10 分钟：结算到账后统一入账",
  },
];

function DimensionCard({ dim }: { dim: Dimension }) {
  const [open, setOpen] = useState(false);
  const Icon = dim.icon;
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-white/[0.03] transition-colors text-left"
      >
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${dim.color}20`, border: `1px solid ${dim.color}40` }}
        >
          <Icon size={13} style={{ color: dim.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-medium text-white truncate">{dim.title}</div>
          <div className="text-[10px] text-gray-500 truncate">{dim.subtitle}</div>
        </div>
        <span
          className="text-[9px] font-mono px-1.5 py-0.5 rounded flex-shrink-0"
          style={{ background: `${dim.color}15`, color: dim.color }}
        >
          {dim.autoLevel.split(" ")[0]}
        </span>
        <ChevronDown size={12} className={`text-gray-500 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-2 border-t border-white/[0.04]">
          <div>
            <div className="text-[9px] font-mono uppercase tracking-wider text-gray-500 mb-1">操作步骤</div>
            <div className="space-y-1">
              {dim.steps.map((s, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[11px] text-gray-300 leading-relaxed">
                  <span className="text-[10px] font-mono flex-shrink-0" style={{ color: dim.color }}>{i + 1}.</span>
                  <span>{s}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <div className="rounded-md px-2 py-1.5 text-[10px] leading-relaxed" style={{ background: `${dim.color}10`, color: dim.color }}>
              自动化：{dim.autoLevel}
            </div>
            <div className="rounded-md px-2 py-1.5 text-[10px] leading-relaxed bg-amber-500/10 text-amber-300">
              人工介入：{dim.human}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AIMoneySOP() {
  const [overviewOpen, setOverviewOpen] = useState(true);
  return (
    <div className="space-y-2">
      {/* 总览 */}
      <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-3">
        <button
          onClick={() => setOverviewOpen(!overviewOpen)}
          className="w-full flex items-center gap-2 text-left"
        >
          <div className="w-7 h-7 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
            <DollarSign size={13} className="text-emerald-400" />
          </div>
          <div className="flex-1">
            <div className="text-[12px] font-medium text-emerald-300">AI 自主赚钱 · 六环飞轮总览</div>
            <div className="text-[10px] text-gray-500">
              策略 → 生产 → 发布 → 变现 → 监测 → 反哺，闭环自动运转；人工每周 30-60 分钟把关
            </div>
          </div>
          <ChevronDown size={12} className={`text-gray-500 flex-shrink-0 transition-transform ${overviewOpen ? "rotate-180" : ""}`} />
        </button>
        {overviewOpen && (
          <div className="mt-2 pt-2 border-t border-emerald-500/10 text-[11px] text-gray-400 leading-relaxed space-y-1">
            <p><span className="text-emerald-300 font-mono text-[10px]">① 策略层</span> GEO+DSO 自动选题（经营中心） → <span className="text-emerald-300 font-mono text-[10px]">② 生产层</span> 内容工厂自动生成（不满意可换标题/换内容） → <span className="text-emerald-300 font-mono text-[10px]">③ 发布层</span> 公众号全自动、短视频人工确认 → <span className="text-emerald-300 font-mono text-[10px]">④ 变现层</span> 自动收入入账看板（财务中心） → <span className="text-emerald-300 font-mono text-[10px]">⑤ 监测层</span> AI 可见性追踪 → <span className="text-emerald-300 font-mono text-[10px]">⑥ 反哺层</span> 数据回流明日计划 → 回到 ①。</p>
            <p className="text-amber-300/80">核心原则：系统干 90% 的活（选题/写稿/发布/统计），人只做 3 件事——审核内容、确认发布、处理询盘成交。</p>
          </div>
        )}
      </div>

      {/* 五个实施维度 */}
      {DIMENSIONS.map((dim) => (
        <DimensionCard key={dim.id} dim={dim} />
      ))}
    </div>
  );
}
